import React, { useEffect, useState } from "react";
import { useCommonContext } from "contexts/CommonContext";
import {
  formatProductQuantity,
  getLabelFromValue,
  productPrice,
} from "common/utils";
import {
  DISPOSAL_TYPE,
  PRODUCT_CATEGORY,
  PRODUCT_STATE,
  SHIP_CHARGE_TYPE,
  SHIP_DAY,
} from "common/constants";
import { FILE_BASE_URL } from "config/apiConfig";
import { Button, message } from "antd";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { createProduct, productCleanData } from "reducers/productSlice";
import { useNavigate } from "react-router-dom";
import CustomConfirmModal from "components/CustomConfirmModal";
import { ProductInfo } from "common/interfaces";

const CheckListDetail = () => {
  const { setHeaderTitle } = useCommonContext();
  const dispatch = useDispatch<AppDispatch>();
  const { createProductData, error } = useSelector(
    (state: RootState) => state.product,
  );
  const { handleSubmit } = useForm();
  const controller = new AbortController();
  const navigate = useNavigate();
  const {
    productInfoContext,
    setProductInfoContext,
    setFileList,
    clearFileList,
  } = useCommonContext();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const onEdit = () => {
    navigate("/seller/product/create");
  };

  const onSubmit = () => {
    if (productInfoContext) {
      dispatch(createProduct(productInfoContext, controller.signal));
      setOpenConfirmModal(false);
    }
  };

  useEffect(() => {
    if (createProductData?.success) {
      message.success("製品の作成に成功しました");
      setFileList([]);
      setProductInfoContext(null);
      dispatch(productCleanData());
      clearFileList();
      sessionStorage.removeItem("productInfo");
      navigate(`/seller/product/${createProductData?.data.code}`);
    }
  }, [createProductData]);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  useEffect(() => {
    const storedProductInfo = sessionStorage.getItem("productInfo");
    if (storedProductInfo) {
      const parsedProductInfo: ProductInfo = JSON.parse(storedProductInfo);
      setProductInfoContext(parsedProductInfo);
    }
  }, [setProductInfoContext]);

  useEffect(() => {
    dispatch(productCleanData());
    setHeaderTitle("出品内容の確認");
    return () => {
      const isCreate = location.pathname.includes("/seller/product/create");
      if (!isCreate) {
        setProductInfoContext(null);
        setFileList([]);
        clearFileList();
        sessionStorage.removeItem("productInfo");
      }
      dispatch(productCleanData());
    };
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      <div className="self-stretch w-full min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[30px] md:gap-10 overflow-hidden">
        <div className="self-stretch justify-start text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
          以下の内容で出品を登録します。ご確認ください。
        </div>
        <div className="self-stretch flex flex-col justify-start items-start gap-[30px] md:gap-12">
          <div className="self-stretch flex flex-col justify-start items-start gap-[30px] md:gap-10">
            <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
              <div
                data-プロパティ1="base + text"
                className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
              >
                <div className="w-1 self-stretch bg-primary rounded-sm" />
                <div className="flex-1 flex justify-start items-center gap-2.5">
                  <div className="flex justify-start items-center gap-[5px]">
                    <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                      出品画像
                    </div>
                    <div className="justify-start text-primary text-[14px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                      （最大5枚）
                    </div>
                  </div>
                </div>
              </div>
              <div className="self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
                <div className="self-stretch inline-flex justify-start items-start gap-5">
                  {productInfoContext?.image_urls.map((path, index) => (
                    <div
                      key={index}
                      className="flex-1 max-w-[90px] md:max-w-[206px] h-[90px] md:h-48 p-2.5 bg-primary-light rounded-md inline-flex flex-col justify-start items-end gap-2.5"
                    >
                      <img
                        src={`${FILE_BASE_URL}/${path}`}
                        alt={`image-${index}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
              <div
                data-プロパティ1="base"
                className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
              >
                <div className="w-1 self-stretch bg-primary rounded-sm" />
                <div className="flex-1 flex justify-start items-center gap-2.5">
                  <div className="flex justify-start items-center gap-[5px]">
                    <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                      商品の基本情報
                    </div>
                  </div>
                </div>
              </div>
              <div className="self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
                <div className="self-stretch rounded-md outline outline-1 outline-offset-[-1px] outline-[#D5D5D5] flex flex-col justify-start items-start overflow-hidden">
                  <div className="self-stretch inline-flex justify-start items-start">
                    <div
                      data-プロパティ1="th"
                      className="w-[130px] md:w-60 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                        商品名
                      </div>
                    </div>
                    <div
                      data-プロパティ1="td"
                      className="flex-1 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                        {productInfoContext?.product_name}
                      </div>
                    </div>
                  </div>
                  <div className="self-stretch inline-flex justify-start items-start">
                    <div
                      data-プロパティ1="th"
                      className="w-[130px] md:w-60 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                        カテゴリー
                      </div>
                    </div>
                    <div
                      data-プロパティ1="td"
                      className="flex-1 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                        {getLabelFromValue(
                          PRODUCT_CATEGORY,
                          productInfoContext?.category_id,
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="self-stretch inline-flex justify-start items-start">
                    <div
                      data-プロパティ1="th"
                      className="w-[130px] md:w-60 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                        数量
                      </div>
                    </div>
                    <div
                      data-プロパティ1="td"
                      className="flex-1 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                        {productInfoContext
                          ? formatProductQuantity(productInfoContext)
                          : ""}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
              <div
                data-プロパティ1="base"
                className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
              >
                <div className="w-1 self-stretch bg-primary rounded-sm" />
                <div className="flex-1 flex justify-start items-center gap-2.5">
                  <div className="flex justify-start items-center gap-[5px]">
                    <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                      出品詳細
                    </div>
                  </div>
                </div>
              </div>
              <div className="self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
                <div className="self-stretch rounded-md outline outline-1 outline-offset-[-1px] outline-[#D5D5D5] flex flex-col justify-start items-start overflow-hidden">
                  <div className="self-stretch inline-flex justify-start items-start">
                    <div
                      data-プロパティ1="th"
                      className="w-[130px] md:w-60 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                        処理区分
                      </div>
                    </div>
                    <div
                      data-プロパティ1="td"
                      className="flex-1 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                        {getLabelFromValue(
                          DISPOSAL_TYPE,
                          productInfoContext?.disposal_type,
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="self-stretch inline-flex justify-start items-start">
                    <div
                      data-プロパティ1="th"
                      className="w-[130px] md:w-60 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                        商品の状態
                      </div>
                    </div>
                    <div
                      data-プロパティ1="td"
                      className="flex-1 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                        {getLabelFromValue(
                          PRODUCT_STATE,
                          productInfoContext?.state,
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="self-stretch inline-flex justify-start items-start">
                    <div
                      data-プロパティ1="th"
                      className="w-[130px] md:w-60 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                        商品の説明
                      </div>
                    </div>
                    <div
                      data-プロパティ1="td"
                      className="flex-1 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                        {productInfoContext?.description}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
              <div
                data-プロパティ1="base"
                className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
              >
                <div className="w-1 self-stretch bg-primary rounded-sm" />
                <div className="flex-1 flex justify-start items-center gap-2.5">
                  <div className="flex justify-start items-center gap-[5px]">
                    <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                      配送について
                    </div>
                  </div>
                </div>
              </div>
              <div className="self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
                <div className="self-stretch rounded-md outline outline-1 outline-offset-[-1px] outline-[#D5D5D5] flex flex-col justify-start items-start overflow-hidden">
                  <div className="self-stretch inline-flex justify-start items-start">
                    <div
                      data-プロパティ1="th"
                      className="w-[130px] md:w-60 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                        配送料の負担
                      </div>
                    </div>
                    <div
                      data-プロパティ1="td"
                      className="flex-1 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                        {getLabelFromValue(
                          SHIP_CHARGE_TYPE,
                          productInfoContext?.shipping_charge_type,
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="self-stretch inline-flex justify-start items-start">
                    <div
                      data-プロパティ1="th"
                      className="w-[130px] md:w-60 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                        発送までの日数
                      </div>
                    </div>
                    <div
                      data-プロパティ1="td"
                      className="flex-1 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                        {getLabelFromValue(
                          SHIP_DAY,
                          productInfoContext?.shipping_days,
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
              <div
                data-プロパティ1="base"
                className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
              >
                <div className="w-1 self-stretch bg-primary rounded-sm" />
                <div className="flex-1 flex justify-start items-center gap-2.5">
                  <div className="flex justify-start items-center gap-[5px]">
                    <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                      販売価格
                    </div>
                  </div>
                </div>
              </div>
              <div className="self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
                <div className="self-stretch rounded-md outline outline-1 outline-offset-[-1px] outline-[#D5D5D5] flex flex-col justify-start items-start overflow-hidden">
                  <div className="self-stretch inline-flex justify-start items-start">
                    <div
                      data-プロパティ1="th"
                      className="w-[130px] md:w-60 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                        価格
                      </div>
                    </div>
                    <div
                      data-プロパティ1="td"
                      className="flex-1 self-stretch min-h-11 px-3.5 py-2.5 bg-white outline outline-1 outline-[#D5D5D5] flex justify-start items-center gap-2.5"
                    >
                      <div className="flex-1 justify-start text-black text-[14px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                        {productPrice(productInfoContext)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
            <div className="self-stretch justify-start">
              <span className="text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                禁止されている行為及び出品物を必ずご確認ください。
                <br />
                また、
              </span>
              <span className="text-black text-base font-normal font-['Noto_Sans_JP'] underline leading-relaxed">
                利用規約
              </span>
              <span className="text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                {" "}
                及び{" "}
              </span>
              <span className="text-black text-base font-normal font-['Noto_Sans_JP'] underline leading-relaxed">
                プライバシーポリシー
              </span>
              <span className="text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                {" "}
                に同意の上、「この内容で出品する」ボタンを押してください。
              </span>
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-3.5">
              <div className="flex-col-reverse md:flex-row self-stretch inline-flex justify-start items-start gap-3.5">
                <Button
                  block
                  type="primary"
                  htmlType="button"
                  className="max-w-[340px] min-h-[60px] bg-primary-light text-primary text-lg font-bold"
                  onClick={onEdit}
                >
                  修正する
                </Button>
                <Button
                  block
                  type="primary"
                  htmlType="button"
                  className="max-w-[340px] min-h-[60px] bg-primary text-lg font-bold"
                  onClick={() => setOpenConfirmModal(true)}
                >
                  この内容で出品する
                </Button>
              </div>
              <div className="self-stretch flex flex-col justify-start items-start gap-[5px]">
                <div className="self-stretch justify-start text-black text-[11px] md:text-xs font-normal font-['Noto_Sans_JP'] leading-tight">
                  ※出品後、マイページの「出品内容の管理」に反映されます。
                </div>
                <div className="self-stretch justify-start text-black text-[11px] md:text-xs font-normal font-['Noto_Sans_JP'] leading-tight">
                  ※出品内容は後から編集・削除できます。
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomConfirmModal
        open={openConfirmModal}
        title="この内容で出品しますか？"
        description={`商品の出品を確定すると、購入希望者が\nこの情報を閲覧・問い合わせできるようになります。\n内容に誤りがないか、今一度ご確認ください。`}
        onOk={onSubmit}
        onCancel={() => setOpenConfirmModal(false)}
        okText="この内容で出品する"
        cancelText="戻る"
        okButtonBgColor="bg-primary"
      />
    </form>
  );
};

export default CheckListDetail;
